import { useMemo } from "react";
import { Carousel } from "primereact/carousel";
import { Skeleton } from "primereact/skeleton";
import { buildResponsiveOptions } from "sj-ui-toolkit/src/helpers/carouselHelpers";
import { AssessmentCard } from "../AssessmentContent/AssessmentCard";
import { TournamentCard } from "./TournamentCard";
import { AssignmentCard } from "./AssignmentCard";
import carouselStyles from "../CarouselOverrides.module.scss";

const lowestThreshold = 1000;
const interval = 400;
const maxCards = 6;

export const TodoCarousel = ({
  assessments = [],
  tournaments = [],
  assignments = [],
  isLoading = false,
}) => {
  const responsiveOptions = buildResponsiveOptions({ lowestThreshold, maxCards, interval });

  // Combine and sort all todos by due date/finish date
  const combinedTodos = useMemo(() => {
    const todos = [];
    const now = Date.now();

    // Add assessments with type identifier
    assessments.forEach(assessment => {
      const dueTime = assessment.dueDate ? new Date(assessment.dueDate).getTime() : null;
      todos.push({
        ...assessment,
        todoType: "assessment",
        sortDate: dueTime,
        isPastDue: dueTime ? dueTime < now : false,
      });
    });

    // Add tournaments with type identifier
    tournaments.forEach(tournament => {
      const finishTime = tournament.finishDate ? new Date(tournament.finishDate).getTime() : null;
      todos.push({
        ...tournament,
        todoType: "tournament",
        sortDate: finishTime,
        isPastDue: finishTime ? finishTime < now : false,
      });
    });

    // Add assignments with type identifier
    assignments.forEach(assignment => {
      const dueTime = assignment.dueDate ? new Date(assignment.dueDate).getTime() : null;
      todos.push({
        ...assignment,
        todoType: "assignment",
        sortDate: dueTime,
        isPastDue: dueTime ? dueTime < now : false,
      });
    });

    // Sort: upcoming items first (by date), then past due items (by date), then items without dates
    return todos.sort((a, b) => {
      // Items without dates go to the end
      if (a.sortDate === null && b.sortDate === null) return 0;
      if (a.sortDate === null) return 1;
      if (b.sortDate === null) return -1;

      // If one is past due and the other isn't, upcoming comes first
      if (a.isPastDue && !b.isPastDue) return 1;
      if (!a.isPastDue && b.isPastDue) return -1;

      // Both are either past due or upcoming, sort by date
      return a.sortDate - b.sortDate;
    });
  }, [assessments, tournaments, assignments]);

  if (combinedTodos.length === 0 && !isLoading) {
    return null;
  }

  const renderTodoCard = (todo, index) => {
    switch (todo.todoType) {
      case "assessment":
        return <AssessmentCard key={`assessment-${index}`} {...todo} />;
      case "tournament":
        return <TournamentCard key={`tournament-${index}`} {...todo} />;
      case "assignment":
        return <AssignmentCard key={`assignment-${index}`} {...todo} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="mt-8">
        <h5 className="atlas-h5 pb-4">TODOs</h5>
        <div className="flex gap-4">
          <Skeleton width="100%" height="350px" borderRadius="8px" />
          <Skeleton width="100%" height="350px" borderRadius="8px" className="hidden md:block" />
          <Skeleton width="100%" height="350px" borderRadius="8px" className="hidden lg:block" />
          <Skeleton width="100%" height="350px" borderRadius="8px" className="hidden xl:block" />
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-8 ${carouselStyles.carouselSection}`}>
      <h5 className={`atlas-h5 pb-4 ${carouselStyles.sectionHeader}`}>
        TODOs ({combinedTodos.length})
      </h5>
      <Carousel
        className={carouselStyles.customCarousel}
        value={combinedTodos}
        responsiveOptions={responsiveOptions}
        numVisible={2}
        numScroll={1}
        showNavigators={true}
        itemTemplate={renderTodoCard}
      />
    </div>
  );
};
